import {
  CLSThresholds,
  FCPThresholds,
  INPThresholds,
  LCPThresholds,
  TTFBThresholds,
  onCLS,
  onINP,
  onLCP,
} from "web-vitals"
import { getAppInsights } from "@/utils/appInsights"

// Sourced from web-vitals so the rating boundaries track the spec on upgrade.
// FID is local: v6 dropped onFID and stopped publishing its thresholds, but
// Next.js still reports the metric from its own bundled copy of the library.
const METRIC_RATING_THRESHOLDS = {
  CLS: CLSThresholds,
  FCP: FCPThresholds,
  FID: [100, 300],
  INP: INPThresholds,
  LCP: LCPThresholds,
  TTFB: TTFBThresholds,
}

const INITIAL_LANDING_SCHEMA_VERSION = "2"
const INITIAL_LANDING_METRIC_STREAM = "initial-landing"
const TRACKED_METRICS = new Set(["LCP", "INP", "CLS"])
const DIAGNOSTIC_METRICS = new Set(["FCP", "TTFB", "FID"])
// Framework timings the pre-refactor reporter forwarded. Next.js-hydration is
// excluded because it was excluded then too; adding it would be new data.
const FRAMEWORK_TIMING_METRICS = new Set([
  "Next.js-before-hydration",
  "Next.js-render",
  "Next.js-route-change-to-render",
])
// These fire on every client-side navigation, so page attribution has to come
// from live sources; __NEXT_DATA__ only ever describes the initial document.
const PER_NAVIGATION_METRICS = new Set([
  "Next.js-render",
  "Next.js-route-change-to-render",
])
const FRAMEWORK_TIMING_STREAM = "framework-timing"
const INTERACTION_PROXY_METRIC = "InteractionLatency"
const INTERACTION_PROXY_STREAM = "interaction-proxy"
// pointerup and keydown cover touch, mouse and keyboard without the double
// count that including click would introduce for pointer-driven activation.
const INTERACTION_EVENTS = ["pointerup", "keydown"]
const QUEUEABLE_METRICS = new Set([
  ...TRACKED_METRICS,
  INTERACTION_PROXY_METRIC,
])
const CORE_VITAL_ENTRY_TYPES = [
  "largest-contentful-paint",
  "layout-shift",
  "event",
]
const UNKNOWN_PAGE_TYPE = "unknown-pagetype"
const UNKNOWN_PAGE_TEMPLATE = "unknown-template"

// Diagnostic metrics arrive from Next's reporter, which has no landing-window
// queue of its own, so they buffer here until the SDK finishes loading.
// Next.js-render and Next.js-route-change-to-render fire on every client-side
// navigation, so the queue is capped: without it, a session where the SDK
// never initializes would retain one payload per route change indefinitely.
const MAX_PENDING_DIAGNOSTIC_METRICS = 20
const pendingDiagnosticMetrics = []
// Drops to false once initialization has definitively failed, so later
// navigations stop paying to build payloads nothing can ever send.
let diagnosticBufferingEnabled = true

/**
 * AEM authors the root pageType inconsistently: clean on some templates
 * ("home"), the page title on others ("studio mcgee bathroom kitchen
 * collection"), which makes it unbounded as a grouping dimension. templateName
 * is a small finite set, so both are emitted and queries can pick whichever is
 * populated. No live source exists for it, only the initial document.
 * @param {object} pageProps Next page props.
 * @return {string} AEM template name, or empty when unavailable.
 */
const getAemPageTemplate = (pageProps = {}) =>
  pageProps?.templateName ||
  pageProps?.tmpData?.templateName ||
  pageProps?.pageModelData?.templateName ||
  ""

const getAemPageType = (pageProps = {}) =>
  pageProps?.gbhDataLayerCustom?.pageType ||
  pageProps?.pageType ||
  pageProps?.pageModelData?.pageType ||
  pageProps?.tmpData?.pageType ||
  pageProps?.gbhDataLayerCustom?.eventPageType ||
  pageProps?.eventPageType ||
  ""

const getWebVitalRating = ({ name = "", value = 0, rating } = {}) => {
  if (rating) return rating

  const limits = METRIC_RATING_THRESHOLDS[name]
  if (!limits) return "unknown"
  if (value <= limits[0]) return "good"
  if (value <= limits[1]) return "needs-improvement"
  return "poor"
}

/**
 * User-Agent Client Hints is Chromium-only and is not rewritten by a plain UA
 * override, so its platform outranks the user-agent string. Without this,
 * DevTools device emulation on a desktop reports itself as real iOS traffic.
 * @param {object} navigatorFields destructured navigator values.
 * @return {string} normalized OS family.
 */
const getOsFamily = ({ userAgentData, userAgent, platform, isIOS }) => {
  const hinted = userAgentData?.platform || ""
  if (hinted) {
    if (/Android/i.test(hinted)) return "android"
    // UA-CH publishes both "Chrome OS" and "Chromium OS"; match either so the
    // hinted path is self-sufficient rather than relying on the UA fallback.
    if (/CrOS|Chrom(?:e|ium) OS/i.test(hinted)) return "chromeos"
    if (/iOS/i.test(hinted)) return "ios"
    if (/mac/i.test(hinted)) return "macos"
    if (/win/i.test(hinted)) return "windows"
    if (/linux/i.test(hinted)) return "linux"
  }

  if (isIOS) return "ios"

  const source = `${platform} ${userAgent}`

  // Android before Linux: Android user agents also advertise Linux.
  if (/Android/i.test(source)) return "android"
  if (/CrOS|Chrome OS/i.test(source)) return "chromeos"
  if (/Mac|Darwin/i.test(source)) return "macos"
  if (/Win/i.test(source)) return "windows"
  if (/Linux|X11/i.test(source)) return "linux"

  return "other"
}

const getBrowserEngine = ({ userAgentData, userAgent, isIOS }) => {
  // Checked before the iOS user-agent test: every iOS browser is WKWebView and
  // none expose User-Agent Client Hints, so its presence proves Chromium even
  // when the user-agent string claims an iPhone (device emulation, spoofing).
  if (userAgentData) return "blink"
  // Real iOS: CriOS, FxiOS and EdgiOS are all WKWebView regardless of brand.
  if (isIOS) return "webkit"
  if (/Firefox\/|Gecko\/\d/i.test(userAgent)) return "gecko"
  if (/Edg\/|Chrome\/|Chromium\//i.test(userAgent)) return "blink"
  if (/Safari\//i.test(userAgent)) return "webkit"

  return "unknown"
}

const getUserAgentDeviceType = (navigatorObject = {}) => {
  const {
    userAgentData,
    userAgent = "",
    platform = "",
    maxTouchPoints = 0,
  } = navigatorObject

  const isIPad =
    /iPad/i.test(userAgent) ||
    (/Macintosh/i.test(userAgent) && maxTouchPoints > 1)
  const isIOS = isIPad || /iPhone|iPod/i.test(userAgent)
  const osFamily = getOsFamily({ userAgentData, userAgent, platform, isIOS })
  const engine = getBrowserEngine({ userAgentData, userAgent, isIOS })

  if (typeof userAgentData?.mobile === "boolean") {
    return {
      deviceCategory: userAgentData.mobile ? "mobile" : "desktop",
      deviceClassificationSource: "user-agent-client-hints",
      platform: userAgentData.platform || platform || "unknown",
      osFamily,
      engine,
    }
  }

  const isMobile =
    isIOS || /Android|IEMobile|Mobile|Opera Mini|FxiOS|CriOS/i.test(userAgent)

  return {
    deviceCategory: isMobile ? "mobile" : "desktop",
    deviceClassificationSource: "user-agent",
    platform: platform || "unknown",
    osFamily,
    engine,
  }
}

const createNavigationId = windowObject => {
  if (typeof windowObject.crypto?.randomUUID === "function") {
    return windowObject.crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const getNavigationType = windowObject =>
  windowObject.performance?.getEntriesByType?.("navigation")?.[0]?.type ||
  "navigate"

/**
 * Feature detection rather than engine sniffing: the day WebKit ships these
 * entry types the reported support level changes without a code edit.
 * @param {Window} windowObject host window.
 * @return {"full"|"partial"|"none"} Core Web Vitals observability level.
 */
const getCoreVitalsSupport = (windowObject = {}) => {
  const supportedEntryTypes =
    windowObject.PerformanceObserver?.supportedEntryTypes || []
  const available = CORE_VITAL_ENTRY_TYPES.filter(entryType =>
    supportedEntryTypes.includes(entryType)
  ).length

  if (available === CORE_VITAL_ENTRY_TYPES.length) return "full"
  if (available === 0) return "none"
  return "partial"
}

const isMetricWithinBoundary = (metric, boundaryTime) => {
  const entries = Array.isArray(metric.entries) ? metric.entries : []

  if (!entries.length) return false

  return entries.every(
    entry =>
      typeof entry.startTime === "number" && entry.startTime <= boundaryTime
  )
}

const scheduleAfterPendingCallbacks = (windowObject, callback) => {
  const requestIdleCallback =
    windowObject.requestIdleCallback?.bind(windowObject)
  const cancelIdleCallback = windowObject.cancelIdleCallback?.bind(windowObject)
  const schedule = requestIdleCallback
    ? scheduledCallback =>
        requestIdleCallback(scheduledCallback, { timeout: 1000 })
    : scheduledCallback => windowObject.setTimeout(scheduledCallback, 0)
  const cancel = requestIdleCallback
    ? taskId => cancelIdleCallback?.(taskId)
    : taskId => windowObject.clearTimeout(taskId)
  let firstTaskId = null
  let secondTaskId = null

  firstTaskId = schedule(() => {
    firstTaskId = null
    secondTaskId = schedule(callback)
  })

  return () => {
    if (firstTaskId !== null) cancel(firstTaskId)
    if (secondTaskId !== null) cancel(secondTaskId)
  }
}

/**
 * requestAnimationFrame runs before the next paint and a macrotask queued from
 * it runs after painting completes. Bracketing the two approximates Event
 * Timing's presentation timestamp on engines that do not implement it.
 * @param {Window} windowObject host window.
 * @param {Function} onRenderStart invoked immediately before paint.
 * @param {Function} onPainted invoked once the frame has been presented.
 * @return {boolean} whether the measurement could be scheduled.
 */
const scheduleNextPaintMeasurement = (
  windowObject,
  onRenderStart,
  onPainted
) => {
  const requestFrame = windowObject.requestAnimationFrame?.bind(windowObject)
  const scheduleTask = windowObject.setTimeout?.bind(windowObject)

  if (!requestFrame || !scheduleTask) return false

  requestFrame(() => {
    onRenderStart()
    scheduleTask(onPainted, 0)
  })

  return true
}

/**
 * Every capture entry point returns a disposer carrying a `flush` hook, so
 * callers can drain queued payloads once the lazily loaded SDK arrives without
 * first checking which path produced the disposer.
 * @return {Function} inert disposer with a no-op flush.
 */
const createNoopCapture = () => {
  const noop = () => {}
  noop.flush = () => {}
  return noop
}

const createInitialLandingWebVitalsCapture = ({
  router,
  webVitals = { onCLS, onINP, onLCP },
  getAppInsightsInstance = getAppInsights,
  windowObject,
  documentObject,
  now,
  pageType = UNKNOWN_PAGE_TYPE,
  pageTemplate = UNKNOWN_PAGE_TEMPLATE,
  getPageType,
  scheduleInpFinalization = callback =>
    scheduleAfterPendingCallbacks(windowObject, callback),
  scheduleNextPaint = (onRenderStart, onPainted) =>
    scheduleNextPaintMeasurement(windowObject, onRenderStart, onPainted),
} = {}) => {
  if (!windowObject || !documentObject) return createNoopCapture()

  const resolvePageType = (preferDynamicPageType = false) => {
    const dynamicPageType =
      typeof getPageType === "function" ? getPageType() : ""
    const resolvedPageType = preferDynamicPageType
      ? dynamicPageType || pageType
      : pageType || dynamicPageType

    return typeof resolvedPageType === "string" && resolvedPageType.trim()
      ? resolvedPageType.trim()
      : UNKNOWN_PAGE_TYPE
  }

  const createContext = (preferDynamicPageType = false) => {
    const initialPath = windowObject.location?.pathname || ""
    const {
      deviceCategory,
      deviceClassificationSource,
      platform,
      osFamily,
      engine,
    } = getUserAgentDeviceType(windowObject.navigator)

    return {
      schemaVersion: INITIAL_LANDING_SCHEMA_VERSION,
      metricStream: INITIAL_LANDING_METRIC_STREAM,
      navigationId: createNavigationId(windowObject),
      initialPath,
      initialUrl: `${windowObject.location?.origin || ""}${initialPath}`,
      pageType: resolvePageType(preferDynamicPageType),
      pageTemplate:
        typeof pageTemplate === "string" && pageTemplate.trim()
          ? pageTemplate.trim()
          : UNKNOWN_PAGE_TEMPLATE,
      navigationType: getNavigationType(windowObject),
      coreVitalsSupport: getCoreVitalsSupport(windowObject),
      deviceCategory,
      deviceClassificationSource,
      osFamily,
      engine,
      deviceType: {
        isMobile: deviceCategory === "mobile",
        platform,
      },
    }
  }

  let context = createContext()
  const latestMetrics = new Map()
  const pendingMetrics = new Map()
  const sentMetrics = new Set()
  // PerformanceEntry.startTime uses the performance timeline, never epoch time.
  const getNow = now || (() => windowObject.performance?.now?.())
  let closedAt = null
  let disposed = false
  let cancelInpFinalization = null
  let landingFinalInpValue = null
  let worstInteraction = null
  let interactionCount = 0

  const flushPendingMetrics = () => {
    const appInsightsInst = getAppInsightsInstance()
    if (!appInsightsInst) return

    pendingMetrics.forEach((payload, eventKey) => {
      try {
        appInsightsInst.trackMetric(payload)
        pendingMetrics.delete(eventKey)
      } catch {
        // Keep the payload queued for the next lifecycle callback.
      }
    })
  }

  const queueMetric = (
    metric,
    {
      logicalName = metric?.name,
      telemetryName = metric?.name,
      metricVariant,
      metricStream = context.metricStream,
      extraProperties = {},
      allowResend = false,
    } = {}
  ) => {
    const { name = "", value, id = "", delta = 0, rating = "unknown" } = metric

    if (!QUEUEABLE_METRICS.has(name)) return
    if (!allowResend && sentMetrics.has(logicalName)) return

    const eventKey = `${context.navigationId}:${logicalName}`
    const properties = {
      // Spread first so shared identity dimensions always win.
      ...extraProperties,
      schemaVersion: context.schemaVersion,
      metricStream,
      metricName: telemetryName,
      name: telemetryName,
      sourceMetricName: name,
      ...(metricVariant ? { metricVariant } : {}),
      metricId: id,
      id,
      value,
      delta,
      rating,
      navigationId: context.navigationId,
      eventKey,
      initialPath: context.initialPath,
      path: context.initialPath,
      url: context.initialUrl,
      pageType: context.pageType,
      pageTemplate: context.pageTemplate,
      navigationType: metric.navigationType || context.navigationType,
      coreVitalsSupport: context.coreVitalsSupport,
      deviceCategory: context.deviceCategory,
      deviceClassificationSource: context.deviceClassificationSource,
      osFamily: context.osFamily,
      engine: context.engine,
      platform: context.deviceType.platform,
      // Legacy nested shape retained for existing queries; prefer deviceCategory + platform.
      deviceType: context.deviceType,
    }

    sentMetrics.add(logicalName)
    pendingMetrics.set(eventKey, {
      name: `Web Vitals - ${telemetryName}`,
      average: value,
      properties,
    })
    flushPendingMetrics()
  }

  const finalizeInteractionProxy = ({ allowResend = false } = {}) => {
    if (!worstInteraction) return

    queueMetric(
      {
        name: INTERACTION_PROXY_METRIC,
        value: Math.round(worstInteraction.duration),
      },
      {
        logicalName: "interaction-latency-proxy",
        telemetryName: "Interaction Latency",
        metricVariant: "next-paint-proxy",
        metricStream: INTERACTION_PROXY_STREAM,
        allowResend,
        extraProperties: {
          inputDelay: Math.round(worstInteraction.inputDelay),
          processingDuration: Math.round(worstInteraction.processingDuration),
          presentationDelay: Math.round(worstInteraction.presentationDelay),
          interactionCount,
        },
      }
    )
  }

  // The two series supersede independently: correcting one must not re-emit a
  // duplicate of the other under its already-committed eventKey.
  const finalizeInteractions = ({
    allowInpResend = false,
    allowProxyResend = false,
  } = {}) => {
    cancelInpFinalization?.()
    cancelInpFinalization = null

    const inpMetric = latestMetrics.get("INP")
    if (inpMetric) landingFinalInpValue = inpMetric.value

    queueMetric(inpMetric || {}, {
      logicalName: "landing-final-inp",
      telemetryName: "INP",
      metricVariant: "landing-final",
      allowResend: allowInpResend,
    })
    finalizeInteractionProxy({ allowResend: allowProxyResend })
    flushPendingMetrics()
  }

  const recordInteraction = sample => {
    if (disposed) return
    // Same hard-landing boundary as INP: the interaction must have begun
    // before the route changed, even if it paints afterwards.
    if (closedAt !== null && sample.startTime > closedAt) return

    interactionCount += 1

    const isNewWorst =
      !worstInteraction || sample.duration > worstInteraction.duration
    if (isNewWorst) worstInteraction = sample

    if (closedAt === null) return

    // Mirrors the INP supersede path: the deferred finalize can commit before
    // the navigating click paints, and that click is usually the worst one.
    if (sentMetrics.has("interaction-latency-proxy")) {
      if (isNewWorst) finalizeInteractions({ allowProxyResend: true })
      return
    }

    if (!cancelInpFinalization) finalizeInteractions()
  }

  const handleInteraction = event => {
    if (disposed) return

    const handlerStart = getNow()
    if (typeof handlerStart !== "number") return

    // event.timeStamp is the hardware dispatch time on modern engines. Guard
    // against legacy epoch-based values before deriving input delay from it.
    const eventTime =
      typeof event.timeStamp === "number" &&
      event.timeStamp > 0 &&
      event.timeStamp <= handlerStart
        ? event.timeStamp
        : handlerStart
    let renderStart = handlerStart

    scheduleNextPaint(
      () => {
        const frameStart = getNow()
        if (typeof frameStart === "number") renderStart = frameStart
      },
      () => {
        const painted = getNow()
        if (typeof painted !== "number") return

        recordInteraction({
          startTime: eventTime,
          inputDelay: Math.max(0, handlerStart - eventTime),
          processingDuration: Math.max(0, renderStart - handlerStart),
          presentationDelay: Math.max(0, painted - renderStart),
          duration: Math.max(0, painted - eventTime),
        })
      }
    )
  }

  const closeLanding = ({ deferInp = false } = {}) => {
    if (disposed) return

    if (closedAt !== null) {
      if (!deferInp) finalizeInteractions()
      return
    }

    closedAt = getNow()
    queueMetric(latestMetrics.get("LCP") || {})
    queueMetric(latestMetrics.get("CLS") || {})

    if (deferInp) {
      cancelInpFinalization = scheduleInpFinalization(() => {
        cancelInpFinalization = null
        finalizeInteractions()
      })
    } else {
      finalizeInteractions()
    }

    flushPendingMetrics()
  }

  const handleRouteChangeStart = (url, options) => {
    // Shallow routing keeps the same document and page component, so the user
    // has not left the landing page and initialPath still describes it. PLP
    // filters, PDP variant redirects and search all navigate shallowly.
    if (options?.shallow) return

    closeLanding({ deferInp: true })
  }

  const handleMetric = metric => {
    if (disposed || !TRACKED_METRICS.has(metric?.name)) return

    flushPendingMetrics()

    if (metric.name === "INP") {
      const isEligible =
        closedAt === null || isMetricWithinBoundary(metric, closedAt)

      if (!isEligible) return

      // The deferred finalize can commit before the navigating click reports.
      // A larger eligible interaction supersedes it: eventKey is stable, so a
      // last-write dedupe resolves to the corrected value.
      const alreadyFinalized = sentMetrics.has("landing-final-inp")
      if (alreadyFinalized && !(metric.value > landingFinalInpValue)) return

      if (!latestMetrics.has("INP")) {
        queueMetric(metric, {
          logicalName: "first-interaction-inp",
          telemetryName: "First Interaction INP",
          metricVariant: "first-interaction",
        })
      }

      // reportAllChanges returns the current package-calculated INP. Retain the
      // latest eligible value and finalize it against the hard-landing route.
      latestMetrics.set("INP", metric)

      if (alreadyFinalized) {
        finalizeInteractions({ allowInpResend: true })
      } else if (closedAt !== null && !cancelInpFinalization) {
        finalizeInteractions()
      }
      return
    }

    if (closedAt === null) {
      latestMetrics.set(metric.name, metric)
      return
    }

    if (
      !sentMetrics.has(metric.name) &&
      isMetricWithinBoundary(metric, closedAt)
    ) {
      queueMetric(metric)
    }
  }

  const handleVisibilityChange = () => {
    if (documentObject.visibilityState === "hidden") closeLanding()
  }

  const handlePageShow = event => {
    if (!event.persisted || disposed) return

    cancelInpFinalization?.()
    cancelInpFinalization = null
    flushPendingMetrics()
    latestMetrics.clear()
    sentMetrics.clear()
    closedAt = null
    landingFinalInpValue = null
    worstInteraction = null
    interactionCount = 0
    context = createContext(true)
  }

  let routeListenerRegistered = false
  let visibilityListenerRegistered = false
  let pageHideListenerRegistered = false
  let pageShowListenerRegistered = false
  let interactionListenersRegistered = false

  const removeLifecycleListeners = () => {
    if (interactionListenersRegistered) {
      INTERACTION_EVENTS.forEach(eventName => {
        documentObject.removeEventListener(eventName, handleInteraction, true)
      })
      interactionListenersRegistered = false
    }
    if (routeListenerRegistered) {
      router?.events?.off?.("routeChangeStart", handleRouteChangeStart)
      routeListenerRegistered = false
    }
    if (visibilityListenerRegistered) {
      documentObject.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      )
      visibilityListenerRegistered = false
    }
    if (pageHideListenerRegistered) {
      windowObject.removeEventListener("pagehide", closeLanding)
      pageHideListenerRegistered = false
    }
    if (pageShowListenerRegistered) {
      windowObject.removeEventListener("pageshow", handlePageShow)
      pageShowListenerRegistered = false
    }
  }

  try {
    webVitals.onCLS(handleMetric, { reportAllChanges: true })
    webVitals.onINP(handleMetric, { reportAllChanges: true })
    webVitals.onLCP(handleMetric, { reportAllChanges: true })

    router?.events?.on?.("routeChangeStart", handleRouteChangeStart)
    routeListenerRegistered = true
    documentObject.addEventListener("visibilitychange", handleVisibilityChange)
    visibilityListenerRegistered = true
    windowObject.addEventListener("pagehide", closeLanding)
    pageHideListenerRegistered = true
    windowObject.addEventListener("pageshow", handlePageShow)
    pageShowListenerRegistered = true
    // Capture phase runs before page handlers so input delay is not inflated
    // by application code; passive keeps the probe off the scroll path.
    INTERACTION_EVENTS.forEach(eventName => {
      documentObject.addEventListener(eventName, handleInteraction, {
        capture: true,
        passive: true,
      })
    })
    interactionListenersRegistered = true
  } catch (error) {
    disposed = true
    removeLifecycleListeners()
    throw error
  }

  const dispose = () => {
    closeLanding()
    disposed = true
    removeLifecycleListeners()
    flushPendingMetrics()
  }

  // The Application Insights SDK loads lazily, so payloads queued before it
  // arrives need an external drain trigger. Attached to the disposer so the
  // existing single-return contract is unchanged.
  dispose.flush = flushPendingMetrics

  return dispose
}

const startInitialLandingWebVitals = ({
  router,
  pageType,
  pageTemplate,
  getPageType,
} = {}) => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return createNoopCapture()
  }

  return createInitialLandingWebVitalsCapture({
    router,
    pageType,
    pageTemplate,
    getPageType,
    windowObject: window,
    documentObject: document,
  })
}

const trackDiagnosticWebVital = (
  metric = {},
  {
    windowObject = typeof window === "undefined" ? undefined : window,
    getAppInsightsInstance = getAppInsights,
  } = {}
) => {
  const { name = "", value, id = "", delta = 0, navigationType } = metric
  const isFrameworkTiming = FRAMEWORK_TIMING_METRICS.has(name)

  // LCP, CLS and INP also arrive here from Next's reporter; they are rejected
  // because the initial-landing stream owns them.
  if (!windowObject || !(DIAGNOSTIC_METRICS.has(name) || isFrameworkTiming)) {
    return
  }

  const initialPageProps = windowObject.__NEXT_DATA__?.props?.pageProps || {}
  const livePageType =
    windowObject.pageObj?.category?.pageType || windowObject.eventPageType || ""
  const initialPageType = getAemPageType(initialPageProps)
  // Per-navigation metrics must agree with the live path they report against;
  // once-per-load metrics keep the immutable initial-document attribution.
  const isPerNavigation = PER_NAVIGATION_METRICS.has(name)
  const pageType = isPerNavigation
    ? livePageType || initialPageType || UNKNOWN_PAGE_TYPE
    : initialPageType || livePageType || UNKNOWN_PAGE_TYPE
  // No live source exists for the template, so per-navigation metrics would
  // only ever carry the landing document's value. Omit rather than mis-group.
  const pageTemplate = isPerNavigation
    ? ""
    : getAemPageTemplate(initialPageProps) || UNKNOWN_PAGE_TEMPLATE
  const {
    deviceCategory,
    deviceClassificationSource,
    platform,
    osFamily,
    engine,
  } = getUserAgentDeviceType(windowObject.navigator)
  const path = windowObject.location?.pathname || ""
  // Deliberately origin + pathname rather than href: query strings carry search
  // terms, promo codes and tracking params, which would put user-entered text
  // into telemetry and make url an unbounded dimension. Matches the landing
  // stream, so the two are directly comparable.
  const url = `${windowObject.location?.origin || ""}${path}`

  const payload = {
    name: `Web Vitals - ${name}`,
    average: value,
    properties: {
      ...(isFrameworkTiming ? { metricStream: FRAMEWORK_TIMING_STREAM } : {}),
      metricName: name,
      name,
      metricId: id,
      id,
      value,
      delta,
      rating: getWebVitalRating(metric),
      url,
      path,
      pageType,
      ...(pageTemplate ? { pageTemplate } : {}),
      navigationType: navigationType || getNavigationType(windowObject),
      coreVitalsSupport: getCoreVitalsSupport(windowObject),
      deviceCategory,
      deviceClassificationSource,
      osFamily,
      engine,
      platform,
      // Retain the previous query shape while adding deterministic flat fields.
      deviceType: {
        isMobile: deviceCategory === "mobile",
        platform,
      },
    },
  }

  const appInsightsInst = getAppInsightsInstance()
  if (!appInsightsInst) {
    // FCP and TTFB fire around hydration, before the lazily loaded SDK is
    // ready. Without this they would be dropped outright rather than delayed.
    // Drop the newest past the cap rather than the oldest: the once-per-load
    // metrics arrive first and are worth more than the Nth route change.
    if (
      diagnosticBufferingEnabled &&
      pendingDiagnosticMetrics.length < MAX_PENDING_DIAGNOSTIC_METRICS
    ) {
      pendingDiagnosticMetrics.push(payload)
    }
    return
  }

  appInsightsInst.trackMetric(payload)
}

/**
 * Stop buffering diagnostic metrics and release anything already queued.
 * Called when Application Insights initialization has definitively failed, so
 * the queue cannot be drained and would otherwise be retained for the session.
 * @return {void}
 */
const stopDiagnosticBuffering = () => {
  diagnosticBufferingEnabled = false
  pendingDiagnosticMetrics.length = 0
}

/**
 * Drain diagnostic metrics captured before Application Insights finished
 * loading. Safe to call repeatedly; a no-op once the queue is empty.
 * @param {object} options injection seam for tests.
 * @return {void}
 */
const flushDiagnosticWebVitals = ({
  getAppInsightsInstance = getAppInsights,
} = {}) => {
  if (!pendingDiagnosticMetrics.length) return

  const appInsightsInst = getAppInsightsInstance()
  if (!appInsightsInst) return

  pendingDiagnosticMetrics.splice(0).forEach(payload => {
    try {
      appInsightsInst.trackMetric(payload)
    } catch {
      // Drop rather than requeue: these are already stale by definition.
    }
  })
}

export {
  createInitialLandingWebVitalsCapture,
  flushDiagnosticWebVitals,
  getAemPageTemplate,
  getAemPageType,
  getCoreVitalsSupport,
  getUserAgentDeviceType,
  startInitialLandingWebVitals,
  stopDiagnosticBuffering,
  trackDiagnosticWebVital,
}
